<template>
  <div class="form-group">
    <label class="tit" for="interests">Interests & behaviors</label>
    <treeselect
      v-model="store.selectedInterests"
      :multiple="true"
      :options="treeData"
      :value-format="'object'"
      :normalizer="normalizer"
      placeholder="Search for interests & behaviors"
    />
  </div>
</template>

<script>
import Treeselect from "vue3-treeselect";
import "vue3-treeselect/dist/vue3-treeselect.css";
import apiClient from "@/core/buildRequest";
import { useSuperAddStore } from "@/store/superAddStore";

export default {
  name: "AudienceForm",
  components: { Treeselect },
  props: {
    processedCountriesList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      treeData: [],
      store: useSuperAddStore(),
    };
  },
  watch: {
    "store.selectedInterests": {
      immediate: true,
      handler(newVal) {
        this.store.interestsMap = newVal
          .filter((item) => item.real_id)
          .map((item) => ({
            platform: item.platform,
            id: item.real_id,
            fake_id: item.fake_id,
            label: item.label,
          }));
      },
    },
  },
  mounted() {
    this.loadInterests();
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.label,
        real_id: node.real_id,
        fake_id: node.fake_id,
        platform: node.platform,
      };
    },
    async loadInterests() {
      try {
        const selectedNames = JSON.parse(
          localStorage.getItem("selectedNames") || "[]"
        );
        const uniqueCountryCodes = [
          ...new Set(selectedNames.map((c) => c.country_code)),
        ];
        const accounts = JSON.parse(
          localStorage.getItem("superselected_accounts")
        );

        const facebookAccountId = accounts?.facebook?.id;
        const tiktokId = accounts?.tiktok?.id;
        const snapchatAccountId = accounts?.snapchat?.id;

        const hasFacebook = !!facebookAccountId;
        const hasTiktok = !!tiktokId;
        const hasSnapchat = !!snapchatAccountId;

        const params = {};
        if (hasFacebook) {
          params.facebook_ad_account_id = facebookAccountId;
        }
        if (hasTiktok) {
          params.tiktok_ad_account_id = tiktokId;
        }
        if (hasSnapchat) {
          uniqueCountryCodes.forEach((code, index) => {
            params[`snap_country_code[${index}]`] = code;
          });
        }

        const response = await apiClient.get("/super-ad/get/interests", {
          params,
        });

        const raw = response.data.data;
        const grouped = {};

        if (hasFacebook)
          grouped.facebook = {
            id: "facebook",
            label: "facebook",
            children: [],
          };
        if (hasSnapchat)
          grouped.snapchat = {
            id: "snapchat",
            label: "snapchat",
            children: [],
          };
        if (hasTiktok)
          grouped.tiktok = { id: "tiktok", label: "tiktok", children: [] };

        raw.forEach((item) => {
          const platform = item.platform;
          const children = (item.children || []).map((child) => ({
            id: `${platform}_${item.label}_${child.fake_id}`, // unique ID
            real_id: child.id,
            fake_id: child.fake_id,
            label: child.label,
            platform: platform,
          }));

          if (grouped[platform]) {
            grouped[platform].children.push({
              id: `${platform}_${item.label}`,
              label: item.label,
              children: children.length ? children : undefined,
            });
          }
        });

        this.treeData = Object.values(grouped);
      } catch (err) {
        console.error("Error loading interests:", err);
      }
    },
    getData() {
      return { interestsMap: this.store.interestsMap };
    },
  },
};
</script>


<style scoped>
.form-wrapper {
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  margin: auto;
}

h2 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  position: relative;
}

label {
  flex: 0 0 180px;
  font-weight: 500;
}

input[type="text"] {
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #ccc;
  width: 100%;
  cursor: pointer;
}

.dropdown-input {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.dropdown-icon {
  position: absolute;
  right: 10px;
  font-size: 14px;
  pointer-events: none;
  color: #666;
}

.dropdown-panel {
  position: absolute;
  top: 100%;
  left: 180px;
  right: 0;
  background: #f9f9f9;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.25rem;
  z-index: 10;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.category-title {
  font-weight: 600;
  margin: 0.75rem 0 0.25rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.custom-audience-link a {
  display: block;
  padding: 0.65rem 0.85rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  color: #0e8fa8;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.custom-audience-link a:hover {
  background: #f0f8fa;
}
.tit {
  min-width: 14rem;
}
::v-deep(.v-field__outline) {
  display: none !important;
}
::v-deep(.v-input--horizontal) {
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 0.25rem;
}
::v-deep(.v-text-field .v-field__outline) {
  display: none !important;
}
::v-deep(.v-field__field) {
  min-width: 5rem;
}
::v-deep(.vue-treeselect__control) {
  padding-left: 5px;
  padding-right: 5px;
  display: table;
  table-layout: fixed;
  width: 100%;
  height: 2.9rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;
}
</style>
